import { computed, defineCustomElement, getCurrentInstance, onMounted, ref } from "vue";

const FilterOptionSelectorClasses =
{
    Option: "filterOptionSelector__option",
    Radio: "filterOptionSelector__radio",
    OptionDisabled: "filterOptionSelector__option--disabled",
    OptionSelected: "filterOptionSelector__option--selected"
};

interface FilterOption
{
    label: string;
    value: string;
    disabled: boolean;
}

const FilterOptionSelector = /*#__PURE__*/ defineCustomElement({
    name: "sky-filteroptionselector",
    shadowRoot: false,
    inheritAttrs: false,
    setup(_props, { emit, attrs })
    {
        const selectedValue = ref(attrs.dataSelected as string);
        const filterOptions = JSON.parse(attrs.dataOptions as string) as FilterOption[];
        const name = attrs.dataName as string;
        const triggersPageRefresh = (attrs.dataTriggersPageRefresh as string)?.toLowerCase() === "true";
        const buttonRole = triggersPageRefresh ? "link" : "button";
        const announcement = ref<HTMLSpanElement>();
        const labelText = ref<string>();

        // Ensure the container elements are properly referencing the label (RadioButtonGroup does something very similar)
        onMounted(() =>
        {
            const host = getCurrentInstance()!.vnode.el as HTMLElement;
            const element = host.parentElement!;
            const hasAriaLabel = attrs['aria-label'] != null;
            const hasAriaLabelledBy = attrs['aria-labelledby'] != null;

            if (!hasAriaLabel && !hasAriaLabelledBy)
            {
                let label = $.findLabel(name);
                if (label.length == 0 && attrs.id)
                {
                    label = $.findLabel(attrs.id as string);
                }

                if (label.length > 0)
                {
                    const id = label.addIds().attr("id")!;
                    element.setAttribute("aria-labelledby", id);
                    labelText.value = label.text();
                }
            }
        });

        const options = computed(() =>
        {
            return filterOptions.map((o, index) =>
            {
                const isSelected = selectedValue.value === o.value;
                const labelID = `${name}_${index}`;

                return (
                    <label
                        tabindex="0"
                        role={buttonRole}
                        aria-current={isSelected}
                        aria-disabled={o.disabled}
                        class=
                        {{
                            [FilterOptionSelectorClasses.Option]: true,
                            [FilterOptionSelectorClasses.OptionDisabled]: o.disabled,
                            [FilterOptionSelectorClasses.OptionSelected]: isSelected,
                        }}
                        onKeyup={(event: KeyboardEvent) =>
                        {
                            if (o.disabled)
                            {
                                return;
                            }

                            handleKeyup(event)
                        }}
                    >
                        <input
                            type="radio"
                            class={FilterOptionSelectorClasses.Radio}
                            name={name}
                            value={o.value}
                            checked={isSelected}
                            onClick={() =>
                            {
                                if (selectedValue.value !== o.value && !o.disabled)
                                {
                                    selectedValue.value = o.value;
                                    if (!triggersPageRefresh && announcement.value)
                                    {
                                        announcement.value.textContent = `${labelText.value} ${o.label} selected`;
                                    }
                                }
                            }}
                        />
                        <span id={labelID}>{o.label}</span>
                    </label>
                );
            });
        });

        return () =>
        (
            <>
                {options.value}
                {!triggersPageRefresh &&
                    (<span ref={announcement} aria-live="polite" class="sr-only"></span>)
                }
            </>
        );

        function handleKeyup(event: KeyboardEvent)
        {
            if (event.key === "Enter" || event.key === " ")
            {
                const labelElement = event.target as HTMLLabelElement;
                const inputElement = labelElement?.firstChild as HTMLInputElement;
                if (inputElement)
                {
                    inputElement.click();
                }
            }
        }
    }
});

export default FilterOptionSelector;